import { useRef, useCallback } from "react";
import { DrillDownContextProps } from "./internal/DrillDownContext";

/**
 * Hook for programmatic control of DrillDown navigation.
 *
 * Provides imperative methods to control drilldown navigation. Use this hook when you need
 * to programmatically navigate between drilldown screens or trigger navigation from custom
 * buttons outside the DrillDown.NextButton and DrillDown.PrevButton components.
 *
 * The drillDownProps must be spread onto Dialog, Drawer, or Page.Panel for the hook to work.
 *
 * Features:
 * - Programmatic navigation control
 * - Direct index setting
 * - Forward and backward navigation methods
 *
 * @returns Object containing navigation methods and props:
 * - setIndex: Function to jump to specific drilldown index
 * - back: Function to navigate to previous index
 * - next: Function to navigate to next index
 * - drillDownProps: Props to spread onto Dialog, Drawer, or Page.Panel
 *
 * @example
 * ```tsx
 * const { setIndex, back, next, drillDownProps } = useDrillDown();
 *
 * <Dialog {...drillDownProps}>
 *   <Button onClick={() => setIndex(2)}>Skip to step 3</Button>
 *   <Button onClick={back}>Go back</Button>
 *   <Button onClick={next}>Go forward</Button>
 * </Dialog>
 * ```
 */
export function useDrillDown() {
  const contextRef = useRef<DrillDownContextProps | null>(null);

  // we pass this onChange to the parent component
  // so it can pass us back the context value
  const onDrillDownContextChange = useCallback(
    (passedContext: DrillDownContextProps) => {
      contextRef.current = passedContext;
    },
    [],
  );

  const setIndex = useCallback((index: number | undefined) => {
    contextRef.current?.setIndex?.(index);
  }, []);

  const back = useCallback(() => {
    contextRef.current?.back?.();
  }, []);

  const next = useCallback(() => {
    contextRef.current?.next?.();
  }, []);

  return {
    setIndex,
    back,
    next,
    drillDownProps: {
      onDrillDownContextChange,
    },
  };
}
